import React, { useState, useEffect } from 'react';
import { 
  CreditCard, 
  DollarSign, 
  Calendar, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  FileText,
  TrendingUp,
  TrendingDown,
  Filter,
  Download,
  Eye
} from 'lucide-react';
import { formatDate } from '../../utils/format';
import api from '../api';
import { toast } from 'react-toastify';

const EtudiantFinancesTab = ({ etudiant, etudiantId }) => {
  const [financialData, setFinancialData] = useState(null);
  const [versements, setVersements] = useState([]);
  const [annees, setAnnees] = useState([]);
  const [selectedAnnee, setSelectedAnnee] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAnnees();
  }, []);

  useEffect(() => {
    if (selectedAnnee) {
      fetchFinancialData();
    }
  }, [selectedAnnee]);

  const fetchAnnees = async () => {
    try {
      const response = await api.get('/etablissement/annees-academiques/');
      setAnnees(response.data);
      // Sélectionner l'année active par défaut
      const activeYear = response.data.find(annee => annee.status);
      if (activeYear) {
        setSelectedAnnee(activeYear.id);
      } else if (response.data.length > 0) {
        setSelectedAnnee(response.data[0].id);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des années académiques');
    }
  };

  const fetchFinancialData = async () => {
    if (!selectedAnnee) return;
    
    setLoading(true);
    try {
      // Récupérer les données financières pour l'année sélectionnée
      const [inscriptionResponse, versementsResponse] = await Promise.all([
        api.get(`/etudiant/finances/${etudiantId}/`, { params: { annee: selectedAnnee } }),
        api.get(`/etudiant/versements/${etudiantId}/`, { params: { annee: selectedAnnee } })
      ]);

      setFinancialData(inscriptionResponse.data);
      setVersements(versementsResponse.data);
      setError(null);
    } catch (error) {
      setError('Erreur lors du chargement des données financières');
      toast.error('Erreur lors du chargement des données financières');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const StatCard = ({ title, value, icon: Icon, color = 'blue', trend = null }) => (
    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">{title}</p>
          <p className={`text-2xl font-bold text-${color}-600`}>{value}</p>
          {trend && (
            <div className="flex items-center mt-1">
              {trend > 0 ? (
                <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
              ) : (
                <TrendingDown className="h-4 w-4 text-red-500 mr-1" />
              )}
              <span className={`text-sm ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {Math.abs(trend)}%
              </span>
            </div>
          )}
        </div>
        <Icon className={`h-12 w-12 text-${color}-600 opacity-20`} />
      </div>
    </div>
  );

  const ProgressBar = ({ current, total, color = 'blue' }) => {
    const percentage = total > 0 ? (current / total) * 100 : 0;
    return (
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className={`bg-${color}-600 h-2 rounded-full transition-all duration-300`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
    );
  };

  const StatusBadge = ({ status, label }) => {
    const colors = {
      'PAYE': 'bg-green-100 text-green-800',
      'PARTIEL': 'bg-yellow-100 text-yellow-800',
      'IMPAYE': 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        {label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#202654]"></div>
        <span className="ml-3 text-gray-600">Chargement des données financières...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
        <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-2" />
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filtre par année */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Situation Financière</h2>
        <div className="flex items-center space-x-4">
          <div className="flex items-center">
            <Filter className="h-5 w-5 text-gray-400 mr-2" />
            <select
              value={selectedAnnee}
              onChange={(e) => setSelectedAnnee(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#202654] focus:border-transparent"
            >
              <option value="">Sélectionner une année</option>
              {annees.map(annee => (
                <option key={annee.id} value={annee.id}>
                  {annee.libelle} {annee.status && '(Actuelle)'}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {!financialData ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune inscription trouvée</h3>
          <p className="text-gray-600">
            Cet étudiant n'a pas d'inscription pour l'année sélectionnée.
          </p>
        </div>
      ) : (
        <>
          {/* Statistiques générales */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              title="Montant Total"
              value={formatCurrency(financialData.montant_total)}
              icon={DollarSign}
              color="blue"
            />
            <StatCard
              title="Montant Payé"
              value={formatCurrency(financialData.montant_paye)}
              icon={CheckCircle}
              color="green"
            />
            <StatCard
              title="Reste à Payer"
              value={formatCurrency(financialData.montant_impaye)}
              icon={XCircle}
              color="red"
            />
            <StatCard
              title="Progression"
              value={`${financialData.progression_paiement}%`}
              icon={TrendingUp}
              color="purple"
            />
          </div>

          {/* Barre de progression */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Progression des Paiements</h3>
              <span className="text-sm text-gray-600">
                {financialData.progression_paiement}% payé
              </span>
            </div>
            <ProgressBar
              current={financialData.montant_paye}
              total={financialData.montant_total}
              color="green"
            />
            <div className="flex justify-between items-center mt-2 text-sm text-gray-600">
              <span>Payé: {formatCurrency(financialData.montant_paye)}</span>
              <span>Total: {formatCurrency(financialData.montant_total)}</span>
            </div>
          </div>

          {/* Détails des options */}
          {financialData.options_complementaires && financialData.options_complementaires.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Options Complémentaires</h3>
              <div className="space-y-3">
                {financialData.options_complementaires.map((option, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{option.tarif?.libelle}</p>
                      <p className="text-sm text-gray-600">{formatCurrency(option.montant)}</p>
                    </div>
                    <StatusBadge
                      status={option.est_paye ? 'PAYE' : 'IMPAYE'}
                      label={option.est_paye ? 'Payé' : 'Non payé'}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Historique des versements */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Historique des Versements</h3>
              <span className="text-sm text-gray-600">
                {versements.length} versement(s)
              </span>
            </div>
            
            {versements.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Aucun versement enregistré pour cette année</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Montant
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Mode de paiement
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Commentaire
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {versements.map((versement) => (
                      <tr key={versement.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(versement.date_versement)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatCurrency(versement.montant)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {versement.mode_paiement?.libelle || 'Non spécifié'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <StatusBadge
                            status={versement.status ? 'PAYE' : 'IMPAYE'}
                            label={versement.status ? 'Validé' : 'En attente'}
                          />
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                          {versement.commentaire || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button className="text-[#202654] hover:text-[#1a1f45] mr-3">
                            <Eye className="h-4 w-4" />
                          </button>
                          <button className="text-green-600 hover:text-green-800">
                            <Download className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default EtudiantFinancesTab; 